// generateTimestamps and toggleEmployee are commented out — kept for future implementation
// of the "replanlægning" flow in contingency settings.
import { useEffect, useState } from "react";
import api, { API_PATHS } from "../service/api";
import labels from "../utils/labels";
import useDataSelector from "./useDataSelector";
import { useAppDispatch } from "../store/hooks";
import {
  clearGanttChartData,
  setIsContingencyPlanPending,
  setSelectedGanttChartTab,
} from "../store/ganttChart";
import { resetPondooViewStateByScenarioId } from "../store/realTimeUpdates";
import { setNotification } from "../store/toastNotifications";
import { AdjustmentStep, ContingencyPreset } from "../types/contingency";
import { getAdjustmentLines } from "../service/contingencyPresetService";

export type { AdjustmentStep };

type AdjustmentOption = { id: AdjustmentStep; label: string; lines: string[] };

type UseContingencySettingsReturn = {
  selectedAdjustmentStep: AdjustmentStep | null;
  onAdjustmentStepSelect: (step: AdjustmentStep | null) => void;
  adjustmentOptions: AdjustmentOption[];
  handleRunContingency: (freezeTimestamp?: string) => Promise<void>;
  setMinimumAffectedDuration: (value: string) => void;
  minimumAffectedDuration?: string;
  isReady: boolean;
  expandedTransportationTime: boolean;
  setExpandedTransportationTime: (value: boolean) => void;
};

// Benjamin: lifecycle eller loading state kald til backenden skal altid i hooks.
// Andet forretningslogik inkl. kald til backenden der ikke kræver en masse react hooks, skal i service lag.
const useContingencySettings = (): UseContingencySettingsReturn => {
  const dispatch = useAppDispatch();
  const [selectedAdjustmentStep, setSelectedAdjustmentStep] =
    useState<AdjustmentStep | null>(null);
  // Set gives O(1) lookups in the employee filter inside handleRunContingency.
  // Todo: toggle in when later features are added.
  // const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(
  //   new Set(),
  // );
  // const [selectedTime, setSelectedTime] = useState<string>("");
  // const [employees, setEmployees] = useState<Employee[]>([]);

  // Data is fetched by useFetchOnQueryChange (triggered on query param changes)
  // and stored in Redux via ganttChartSlice. useDataSelector reads from that slice,
  // selecting the right dataset based on the active tab (Original vs. Custom).
  const { dataToUse, idScenario } = useDataSelector();

  const [minimumAffectedDuration, setMinimumAffectedDuration] =
    useState<string>();
  const [expandedTransportationTime, setExpandedTransportationTime] =
    useState<boolean>(false);
  const [presets, setPresets] = useState<ContingencyPreset[]>([]);

  useEffect((): void => {
    api
      .get<ContingencyPreset[]>(API_PATHS.getContingencyPresets)
      .then((response): void => setPresets(response.data))
      .catch((): void => {
        dispatch(
          setNotification({ message: labels.contingencyPresetsFetchError }),
        );
      });
  }, [dispatch]);

  const toOption = (id: AdjustmentStep, label: string): AdjustmentOption => ({
    id,
    label,
    lines: getAdjustmentLines(presets, id, expandedTransportationTime),
  });
  const adjustmentOptions: AdjustmentOption[] = [
    toOption("SMALL", labels.smallAdjustment),
    toOption("MEDIUM", labels.mediumAdjustment),
    toOption("LARGE", labels.largeAdjustment),
  ];

  // Todo: toggle in when later features are added.
  // const durationOptions = [
  //   { value: 1.0, label: "100%" },
  //   { value: 0.9, label: "90%" },
  //   { value: 0.8, label: "80%" },
  //   { value: 0.7, label: "70%" },
  //   { value: 0.6, label: "60%" },
  //   { value: 0.5, label: "50%" },
  // ];
  // Todo: toggle in when later features are added.
  // const toggleEmployee = (employeeRef: string) => {
  //   setSelectedEmployees((prev) => {
  //     const next = new Set(prev);
  //     if (next.has(employeeRef)) {
  //       next.delete(employeeRef);
  //     } else {
  //       next.add(employeeRef);
  //     }
  //     return next;
  //   });
  // };

  const handleRunContingency = async (
    freezeTimestamp?: string,
  ): Promise<void> => {
    if (!idScenario || (!selectedAdjustmentStep && !expandedTransportationTime))
      return;

    // Todo: toggle in when later features are added.
    // for (const employee of employees.filter((e) =>
    //   selectedEmployees.has(e.employeeRef),
    // )) {
    //   await EmployeeSettingsService.deleteEmployeeWorkTime(
    //     employee.shiftSchedule.idShift,
    //     dispatch,
    //   );
    // }

    try {
      // POST to trigger optimization — idScenario comes from Redux (see useDataSelector above).
      await api.post<void>(API_PATHS.startOptimization, {
        idScenario,
        contingencyLevel: selectedAdjustmentStep,
        minimumAffectedDuration:
          minimumAffectedDuration !== undefined
            ? Number(minimumAffectedDuration)
            : undefined,
        expandedTransportationTime,
        freezeTimestamp,
      });
      //Clearn ganttChartData to initiate fetch, when data is ready.
      dispatch(clearGanttChartData());
      dispatch(setIsContingencyPlanPending(true));
      dispatch(setSelectedGanttChartTab("Optimised"));
      // Flip the Optimised plan straight to "loading" so the stale plan is
      // hidden immediately (the status bar shows progress). When the new plan
      // finishes, useFetchOnQueryChange refetches on the loading -> ready edge.
      dispatch(resetPondooViewStateByScenarioId(idScenario));
    } catch {
      dispatch(
        setNotification({
          message: labels.nexusExportErrorGeneral,
          type: "send_to_nexus",
        }),
      );
    }
  };

  return {
    selectedAdjustmentStep,
    onAdjustmentStepSelect: setSelectedAdjustmentStep,
    adjustmentOptions,
    handleRunContingency,
    setMinimumAffectedDuration,
    minimumAffectedDuration,
    isReady: !!idScenario,
    expandedTransportationTime,
    setExpandedTransportationTime,
  };
};

export default useContingencySettings;
